import React from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import { Button } from "../ui/button";
import { Search, Menu } from "lucide-react";
import UserMenu from "./UserMenu";
import ThemeToggle from "./ThemeToggle";
import GlobalSearch from "./GlobalSearch";
import NotificationsBell from "../notifications/NotificationsBell";
import HelpMenu from "./HelpMenu";
import PlanQuotaChip from "../billing/PlanQuotaChip";
import RoleBadge from "../users/RoleBadge"; // V2-T7
import { useAuth } from "../../contexts/AuthContext"; // V2-T7
import { api } from "../../lib/api"; // V2-T2
import UniverseSwitch from "./UniverseSwitch";

// V2-T3 — Mapping complet slugs → labels français
// Centralise TOUS les segments de route connus.
const LABELS = {
  // Navigation principale
  dashboard:            "Tableau de bord",
  sites:                "Chantiers",
  "my-sites":           "Mes chantiers",
  map:                  "Carte",
  planning:             "Planning",
  "my-schedule":        "Mon planning",
  timesheets:           "Feuilles d'heures",
  absences:             "Absences",
  memos:                "Mémos",
  users:                "Utilisateurs",
  punch:                "Pointage",
  tasks:                "Tâches",
  // Actions
  new:                  "Nouveau",
  // Paramètres
  settings:             "Paramètres",
  account:              "Mon profil",
  profile:              "Mon profil",
  notifications:        "Notifications",
  company:              "Entreprise",
  billing:              "Facturation",
  "payroll-variables":  "Variables de paie",
  rgpd:                 "RGPD",
  // Aide
  help:                 "Aide",
  plans:                "Plans",
  roles:                "Rôles",
  // Modules métier (B12 / audit 5.3 : jamais de slug brut)
  "couts-mo":              "Coût main-d'œuvre",
  rh:                      "RH",
  soustraitants:           "Sous-traitants",
  "sous-traitant":         "Sous-traitant",
  subcontractors:          "Sous-traitants",
  "notes-de-frais":        "Notes de frais",
  "rapports-intervention": "Rapports d'intervention",
  demandes:                "Demandes",
  stock:                   "Stock",
  jalons:                  "Jalons",
  factures:                "Factures",
  conges:                  "Congés",
  heures:                  "Heures",
  reports:                 "Rapports",
  // Divers
  logout:               "Déconnexion",
};

// V2-T2 — Détection UUID (v4 standard)
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * V2-T2 — BreadcrumbSegment : résout les UUIDs en noms lisibles.
 * - /sites/:id  → fetch le nom du chantier
 * - /users/:id  → fetch le nom de l'utilisateur
 * - Fallback 'Chargement…' pendant le fetch, jamais l'UUID brut.
 * - Cache React Query (5 min) pour éviter les requêtes redondantes.
 */
function BreadcrumbSegment({ segment, prevSegment, siteId, href, isLast }) {
  const isUUID     = UUID_RE.test(segment);
  const isSiteId   = prevSegment === "sites"    && isUUID;
  const isUserId   = prevSegment === "users"    && isUUID;
  const isTaskId   = prevSegment === "tasks"    && isUUID && !!siteId;
  const isDemandeId = prevSegment === "demandes" && isUUID;

  const { data: siteData } = useQuery({
    queryKey: ["breadcrumb:site", segment],
    queryFn:  async () => (await api.get(`/sites/${segment}`)).data,
    enabled:  isSiteId,
    staleTime: 5 * 60 * 1000,
    retry:    false,
  });

  const { data: userData } = useQuery({
    queryKey: ["breadcrumb:user", segment],
    queryFn:  async () => (await api.get(`/users/${segment}`)).data,
    enabled:  isUserId,
    staleTime: 5 * 60 * 1000,
    retry:    false,
  });

  const { data: taskData } = useQuery({
    queryKey: ["breadcrumb:task", siteId, segment],
    queryFn:  async () => (await api.get(`/sites/${siteId}/tasks/${segment}`)).data,
    enabled:  isTaskId,
    staleTime: 5 * 60 * 1000,
    retry:    false,
  });

  const { data: demandeData } = useQuery({
    queryKey: ["breadcrumb:demande", segment],
    queryFn:  async () => (await api.get(`/demandes/${segment}`)).data,
    enabled:  isDemandeId,
    staleTime: 5 * 60 * 1000,
    retry:    false,
  });

  let label;
  if (isSiteId)        label = siteData?.name       || "Chargement…";
  else if (isUserId)   label = userData?.full_name   || "Chargement…";
  else if (isTaskId)   label = taskData?.title       || "Chargement…";
  else if (isDemandeId) label = demandeData?.reference || "Chargement…";
  // B12 : ne jamais afficher un UUID brut ; capitaliser les slugs inconnus.
  else if (isUUID)     label = "Détail";
  else                 label = LABELS[segment] || (segment.charAt(0).toUpperCase() + segment.slice(1));

  if (isLast) {
    return <BreadcrumbPage>{label}</BreadcrumbPage>;
  }
  return (
    <BreadcrumbLink asChild>
      <Link to={href}>{label}</Link>
    </BreadcrumbLink>
  );
}

export default function Topbar({ onOpenSidebar }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user }  = useAuth();         // V2-T7
  const segments  = location.pathname.split("/").filter(Boolean);

  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-30 bg-background/80 backdrop-blur border-b border-border"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/*
       * Mesure a 320 px : cette barre reclamait 328 px pour 320 disponibles.
       * Les boutons d'icone declarent pourtant 44 x 44 (variante `icon`), mais
       * `flex-shrink` vaut 1 par defaut : ils etaient ECRASES a 16 px de large
       * au lieu d'etre trop petits. Poser `shrink-0` sans rendre de la place
       * aurait juste transforme la compression en debordement horizontal.
       * On rend donc la place avant : espacement et marges resserres sous `sm`.
       */}
      <div className="flex items-center gap-1 sm:gap-3 px-2 sm:px-4 md:px-6 h-14">
        {/* Hamburger mobile */}
        <Button
          data-testid="hamburger-btn"
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onOpenSidebar}
          aria-label="Ouvrir le menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* V2-T2+T3 — Breadcrumb avec résolution async des UUIDs */}
        <div className="hidden md:block">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">KNOWROK</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {segments.map((s, idx) => {
                // B12 : 'admin' est un artefact de routage (ex. /admin/soustraitants),
                // pas un niveau métier — on ne l'affiche pas dans le fil d'Ariane.
                if (s === "admin") return null;
                const href        = "/" + segments.slice(0, idx + 1).join("/");
                const isLast      = idx === segments.length - 1;
                const prevSegment = idx > 0 ? segments[idx - 1] : null;
                // Pour résoudre un taskId (/sites/:siteId/tasks/:taskId) il faut le siteId
                const sitesIdx    = segments.indexOf("sites");
                const siteId      = sitesIdx >= 0 ? segments[sitesIdx + 1] : null;
                return (
                  <React.Fragment key={href}>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbSegment
                        segment={s}
                        prevSegment={prevSegment}
                        siteId={siteId}
                        href={href}
                        isLast={isLast}
                      />
                    </BreadcrumbItem>
                  </React.Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="flex-1" />

        {/* Sélecteur d'univers [Chantier | Interventions] (owner/manager,
            flag univers) — même composant dans l'InterventionsLayout. */}
        <UniverseSwitch current="chantier" />

        {/* Recherche */}
        <GlobalSearch />
        <Button
          data-testid="global-search-btn-mobile"
          variant="ghost"
          size="icon"
          className="sm:hidden"
          onClick={() => navigate("/search")}
          aria-label="Rechercher"
        >
          <Search className="h-5 w-5" />
        </Button>

        <div className="hidden sm:block">
          <ThemeToggle compact />
        </div>

        <NotificationsBell />
        {/*
         * Le badge d'abonnement est la seule INFORMATION de cette barre — tout
         * le reste est une commande. A 320 px il coutait 54 px, soit plus qu'un
         * bouton entier : c'est lui qui cede la place pour que les commandes
         * atteignent leurs 44 px. Il reste accessible par le menu utilisateur.
         */}
        <div className="hidden sm:block">
          <PlanQuotaChip />
        </div>
        {/*
         * A 320 px, six commandes a 44 px ne tiennent pas dans la barre : la
         * derniere (le menu utilisateur) sortait de l'ecran et se faisait
         * rogner — sans que `audit-overflow-320` le voie, parce qu'un ancetre
         * masque le depassement au lieu de le faire defiler. Le centre d'aide
         * est la commande la moins urgente au doigt sur un chantier : il quitte
         * la barre sous `sm` et reapparait dans le menu utilisateur, qui, lui,
         * est toujours la. Rien n'est perdu, tout reste a 44 px.
         */}
        <div className="hidden sm:block">
          <HelpMenu />
        </div>

        {/* V2-T7 — Badge de rôle visible dans le header (à côté de l'avatar) */}
        {user?.role && (
          <div className="hidden sm:block" data-testid="topbar-role-badge">
            <RoleBadge role={user.role} />
          </div>
        )}

        <UserMenu />
      </div>
    </header>
  );
}
